import { useState } from "react";
import { motion } from "framer-motion";
import { Crown } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { useOrder } from "./OrderDialog";
import { useLang } from "../i18n";

export const Loyalty = () => {
    const [stamps, setStamps] = useState([true, true, false, false, false]);
    const order = useOrder();
    const { t } = useLang();
    const collected = stamps.filter(Boolean).length;

    return (
        <section id="loyalty" data-testid="loyalty-section" className="relative py-24 sm:py-32 bg-onyx/40 overflow-hidden">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
                <Reveal>
                    <Overline>{t("loyalty.overline")}</Overline>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.05]">
                        {t("loyalty.h1a")} <span className="italic gold-text">{t("loyalty.h1b")}</span>
                    </h2>
                    <p className="mt-6 text-stone-300 font-light leading-relaxed max-w-md">
                        {t("loyalty.body")}
                    </p>
                    <button
                        data-testid="loyalty-order-button"
                        onClick={() => order?.openOrder()}
                        className="mt-8 inline-flex rounded-full bg-saffron px-8 py-3.5 text-sm font-semibold tracking-wide text-ink hover:bg-saffron-bright hover:shadow-[0_0_36px_rgba(217,119,6,0.35)] transition-[background-color,box-shadow] duration-300"
                    >
                        {t("loyalty.cta")}
                    </button>
                </Reveal>

                <Reveal delay={0.15}>
                    <div
                        data-testid="loyalty-stamp-card"
                        className="relative gold-border rounded-3xl bg-elevated/70 backdrop-blur p-7 sm:p-9 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-cinzel text-sm tracking-[0.25em] text-brass">ROYAL TADKA</p>
                                <p className="font-serif italic text-xl text-cream mt-1">{t("loyalty.cardTitle")}</p>
                            </div>
                            <Crown className="w-7 h-7 text-saffron-bright" strokeWidth={1.25} />
                        </div>

                        <div className="mt-8 grid grid-cols-6 gap-2.5 sm:gap-3">
                            {stamps.map((filled, i) => (
                                <motion.button
                                    key={i}
                                    data-testid={`loyalty-stamp-${i + 1}`}
                                    onClick={() =>
                                        setStamps((s) => s.map((v, j) => (j === i ? !v : v)))
                                    }
                                    whileTap={{ scale: 0.85 }}
                                    aria-label={`Stamp ${i + 1}`}
                                    className={`aspect-square rounded-full border flex items-center justify-center transition-colors duration-500 ${
                                        filled
                                            ? "bg-saffron/90 border-saffron shadow-[0_0_20px_rgba(217,119,6,0.4)]"
                                            : "border-dashed border-brass/40 bg-ink/40 hover:border-saffron/60"
                                    }`}
                                >
                                    {filled && (
                                        <motion.span
                                            initial={{ scale: 0, rotate: -30 }}
                                            animate={{ scale: 1, rotate: 0 }}
                                            transition={{ type: "spring", stiffness: 300, damping: 14 }}
                                        >
                                            <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-ink" strokeWidth={2} />
                                        </motion.span>
                                    )}
                                </motion.button>
                            ))}
                            <div
                                data-testid="loyalty-reward-slot"
                                className="aspect-square rounded-full border border-saffron-glow/60 bg-saffron/10 flex items-center justify-center"
                            >
                                <span className="font-serif italic text-saffron-glow text-[10px] sm:text-xs leading-tight text-center px-1">
                                    FREE
                                </span>
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-between text-xs text-stone-400">
                            <span data-testid="loyalty-progress-text">
                                {t("loyalty.progress").replace("{n}", collected)}
                            </span>
                            <span className="text-brass/80">{t("loyalty.reward")}</span>
                        </div>
                        <p className="mt-4 text-[11px] text-stone-500 leading-relaxed">
                            {t("loyalty.note")}
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
